import type { Translate } from 'next-translate';
import type { FC, ReactNode } from 'react';
import Image from 'next/image';
import { AddCircleOutline } from '@eqi-investimentos/icons/web';
import { Button, Flex, type FlexProps, Skeleton, Text, TextProps } from '@eqi-investimentos/react';
import { useGetWalletInstitutions } from '@/modules/Wallet/http/client/useGetWalletInstitutions';
import { formatNumberToPTBRCurrency } from '@/utils/format';
import { IfRender, MapRender } from '@/utils/jsx';
export const MyInstitutionsCard: FC<MyInstitutionsCardProps> = ({
  topRightAction = null,
  propsTitle,
  t,
  openConnectorsDrawer = () => null,
  ...props
}) => {
  const {
    data: institutions = {
      data: [],
      total: 0
    },
    isFetching
  } = useGetWalletInstitutions();
  const hasNoInstitutions = institutions?.data?.length === 0;
  const institutionsListPrimary = institutions?.data.slice(0, 3);
  const institutionsSliced = institutions?.data.slice(3);
  return <Flex display={{
    base: 'none',
    md: 'flex'
  }} flexDir="column" w="full" h="full" gap="2rem" p="2rem" maxH="26.25rem" bgColor="background.primary" border="1px solid" borderColor="transparency.light" borderRadius="xs" {...props} data-sentry-element="Flex" data-sentry-component="MyInstitutionsCard" data-sentry-source-file="MyInstitutionsCard.tsx">
      <Flex justifyContent="space-between" alignItems="center" w="full" data-sentry-element="Flex" data-sentry-source-file="MyInstitutionsCard.tsx">
        <IfRender condition={isFetching} data-sentry-element="IfRender" data-sentry-source-file="MyInstitutionsCard.tsx">
          <Skeleton w="10.625rem" h="1.625rem" data-sentry-element="Skeleton" data-sentry-source-file="MyInstitutionsCard.tsx" />
          <Skeleton w="4.375rem" h="1.25rem" data-sentry-element="Skeleton" data-sentry-source-file="MyInstitutionsCard.tsx" />
        </IfRender>

        <IfRender condition={!isFetching} data-sentry-element="IfRender" data-sentry-source-file="MyInstitutionsCard.tsx">
          <Text fontSize="lg" fontWeight="normal" lineHeight="h6" color="content.primary" {...propsTitle} data-sentry-element="Text" data-sentry-source-file="MyInstitutionsCard.tsx">
            {t('screens.myWallet.chartInstitutions.title')}
          </Text>
        </IfRender>
        <IfRender condition={!!topRightAction && !hasNoInstitutions} data-sentry-element="IfRender" data-sentry-source-file="MyInstitutionsCard.tsx">
          {topRightAction}
        </IfRender>
        <IfRender condition={!hasNoInstitutions && !isFetching && !topRightAction} data-sentry-element="IfRender" data-sentry-source-file="MyInstitutionsCard.tsx">
          <Button variant="naked" onClick={openConnectorsDrawer} color="content.primary" gap="xs" _hover={{
          p: {
            clor: 'content.primary'
          }
        }} rightIcon={<AddCircleOutline width={16} height={16} color="content.brand" />} data-sentry-element="Button" data-sentry-source-file="MyInstitutionsCard.tsx">
            {t('attributes.add')}
          </Button>
        </IfRender>
      </Flex>

      <Flex flexDir="column" w="full" gap="xs" data-sentry-element="Flex" data-sentry-source-file="MyInstitutionsCard.tsx">
        <IfRender condition={isFetching} data-sentry-element="IfRender" data-sentry-source-file="MyInstitutionsCard.tsx">
          <MapRender items={Array.from({
          length: 4
        })} render={(_, idx) => <Skeleton key={idx} w="full" h="3.9375rem" my="0.625rem" />} data-sentry-element="MapRender" data-sentry-source-file="MyInstitutionsCard.tsx" />
        </IfRender>
        <IfRender condition={!isFetching && !hasNoInstitutions} data-sentry-element="IfRender" data-sentry-source-file="MyInstitutionsCard.tsx">
          <MapRender items={institutionsListPrimary} render={(institution, idx) => <Flex key={idx} justifyContent="space-between" alignItems="center" p="l" bg="background.brandLight" borderRadius="2xs">
                <Flex alignItems="center" gap="s" sx={{
            img: {
              w: '1.25rem',
              h: '1.25rem'
            }
          }}>
                  <Image src={institution.thumb} alt={`conector pluggy - ${institution.name}`} width={20} height={20} style={{
              objectFit: 'cover',
              borderRadius: '0.25rem'
            }} onError={({
              currentTarget
            }) => {
              currentTarget.onerror = null;
              currentTarget.src = '/assets/placeholder-content.webp';
            }} />
                  <Text fontSize="md" fontWeight="normal" color="content.primary" lineHeight="7">
                    {institution.name}
                  </Text>
                </Flex>
                <Text fontSize="md" fontWeight="normal" color="content.secondary" className="hide-monetary-right">
                  {formatNumberToPTBRCurrency(institution.total)}
                </Text>
              </Flex>} data-sentry-element="MapRender" data-sentry-source-file="MyInstitutionsCard.tsx" />
        </IfRender>
        <IfRender condition={!isFetching && !hasNoInstitutions && !!institutionsSliced.length} data-sentry-element="IfRender" data-sentry-source-file="MyInstitutionsCard.tsx">
          <Flex justifyContent="space-between" alignItems="center" p="l" bg="background.brandLight" borderRadius="2xs" data-sentry-element="Flex" data-sentry-source-file="MyInstitutionsCard.tsx">
            <Flex alignItems="center" gap="s" data-sentry-element="Flex" data-sentry-source-file="MyInstitutionsCard.tsx">
              <Flex gap="xs" sx={{
              img: {
                w: '1.25rem',
                h: '1.25rem'
              }
            }} data-sentry-element="Flex" data-sentry-source-file="MyInstitutionsCard.tsx">
                <MapRender items={institutionsSliced} render={(institution, idx) => {
                if (idx > 3) return <></>;
                return <Image key={idx} src={institution.thumb} alt={`instituicao financeira - ${institution.name}`} width={20} height={20} style={{
                  objectFit: 'cover',
                  borderRadius: '0.25rem'
                }} onError={({
                  currentTarget
                }) => {
                  currentTarget.onerror = null;
                  currentTarget.src = '/assets/placeholder-content.webp';
                }} />;
              }} data-sentry-element="MapRender" data-sentry-source-file="MyInstitutionsCard.tsx" />
              </Flex>

              <IfRender condition={institutionsSliced.length > 4} data-sentry-element="IfRender" data-sentry-source-file="MyInstitutionsCard.tsx">
                <Text fontSize="xs" fontWeight="light" color="content.secondary" lineHeight="7" data-sentry-element="Text" data-sentry-source-file="MyInstitutionsCard.tsx">
                  {`${t('screens.myWallet.assetsDetails.cardInstitutionDetails.andMore')} ${institutionsSliced.length - 4}`}
                </Text>
              </IfRender>
            </Flex>
            <Text fontSize="md" fontWeight="normal" color="content.secondary" className="hide-monetary-right" data-sentry-element="Text" data-sentry-source-file="MyInstitutionsCard.tsx">
              {formatNumberToPTBRCurrency(institutionsSliced.slice(3).reduce((acc, curr) => acc + curr.total, 0))}
            </Text>
          </Flex>
        </IfRender>
      </Flex>
    </Flex>;
};
type MyInstitutionsCardProps = Omit<FlexProps, 'children'> & {
  propsTitle?: TextProps;
  topRightAction?: ReactNode | null;
  t: Translate;
  openConnectorsDrawer?: () => void;
};